import React from 'react';
import { CheckSquare, Square, Info } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AVAILABLE_SUBTRAMITES } from '../types';

interface SubtramiteSelectorProps {
  selectedSubtramites: string[];
  onChange?: (selectedSubtramites: string[]) => void;
  updateSubtramitesConfiguration: (subtramites: string[]) => void;
}

const SubtramiteSelector: React.FC<SubtramiteSelectorProps> = ({
  selectedSubtramites,
  updateSubtramitesConfiguration
}) => {

  const handleToggleSubtramite = (subtramiteKey: string) => {
    const isSelected = selectedSubtramites.includes(subtramiteKey);
    let newSelection: string[];

    if (isSelected) {
      newSelection = selectedSubtramites.filter(key => key !== subtramiteKey);
    } else {
      newSelection = [...selectedSubtramites, subtramiteKey];
    }

    updateSubtramitesConfiguration(newSelection);
  };

  const handleSelectAll = () => {
    const allKeys = AVAILABLE_SUBTRAMITES.map(sub => sub.key);
    updateSubtramitesConfiguration(allKeys);
  };

  const handleSelectNone = () => {
    updateSubtramitesConfiguration([]);
  };

  const selectedCount = selectedSubtramites.length;
  const totalCount = AVAILABLE_SUBTRAMITES.length;

  return (
    <div className="space-y-6">
      {/* Header with selection info and actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant="outline" className="flex items-center gap-1">
            <CheckSquare className="h-3 w-3" />
            {selectedCount} de {totalCount} seleccionados
          </Badge>
          <span className="text-sm text-gray-600">
            Subtramites que se iniciarán automáticamente
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSelectNone}
            className="text-sm text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
          >
            Deseleccionar todo
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={handleSelectAll}
            className="text-sm text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
          >
            Seleccionar todo
          </button>
        </div>
      </div>

      {/* Info alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Los subtramites seleccionados se crearán automáticamente cuando se inicie un nuevo trámite.
          Los usuarios podrán agregar subtramites adicionales manualmente después.
        </AlertDescription>
      </Alert>

      {/* Subtramites grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {AVAILABLE_SUBTRAMITES.map((subtramite) => {
          const isSelected = selectedSubtramites.includes(subtramite.key);

          return (
            <Card
              key={subtramite.key}
              className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                isSelected
                  ? 'ring-2 ring-primary bg-primary/5 border-primary'
                  : 'hover:border-gray-300'
              }`}
              onClick={() => handleToggleSubtramite(subtramite.key)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={isSelected}
                    onChange={() => {}} // Handled by card click
                    className="pointer-events-none"
                  />
                  <div className="flex-1">
                    <CardTitle className="text-base font-medium">
                      {subtramite.name}
                    </CardTitle>
                  </div>
                  {isSelected && (
                    <Badge variant="default">
                      Activo
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <CardDescription className="text-sm">
                  {subtramite.description}
                </CardDescription>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Selected subtramites preview */}
      {selectedCount > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-blue-900">
              Vista Previa de Inicialización
            </CardTitle>
            <CardDescription className="text-blue-700">
              Estos subtramites se crearán automáticamente al iniciar un nuevo trámite:
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {selectedSubtramites.map((key) => {
                const subtramite = AVAILABLE_SUBTRAMITES.find(sub => sub.key === key);
                return subtramite ? (
                  <Badge
                    key={key}
                    variant="secondary"
                    className="bg-blue-100 text-blue-800 border-blue-300"
                  >
                    {subtramite.name}
                  </Badge>
                ) : null;
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {selectedCount === 0 && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <Square className="h-12 w-12 text-yellow-400 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-yellow-900 mb-2">
                Ningún subtramite seleccionado
              </h3>
              <p className="text-yellow-700 text-sm">
                Sin subtramites seleccionados, los usuarios deberán crear manualmente
                todos los subtramites que necesiten para cada trámite.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SubtramiteSelector;